import { getGlobalLogger } from '@zg-rentals/logger-base';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { isDev, isTest } from '@zg-rentals/environment-utils';

export function getErrorDetails(error: Error | unknown, context?: Record<string, unknown>) {
  const errorMessage =
    error instanceof Error
      ? `Message: "${error.message}" Stack: "${error.stack}"`
      : `No Error instance provided. Serialized result: ${JSON.stringify(error)}`;

  return `${errorMessage}${context ? ` Context: ${JSON.stringify(context)}` : ''}`;
}

export function logError({
  error,
  context,
  errorType,
}: {
  error: Error | unknown;
  context?: string | Record<string, unknown>;
  errorType?: string;
}) {
  try {
    const isServerSide = typeof window === 'undefined';
    const errorPrefix = `${isServerSide ? 'httpServer' : 'browser'} error`;
    const fullContext = typeof context === 'string' ? { details: context } : context;
    const errorDetails = getErrorDetails(error, fullContext);

    // some error names have white space, like Apollo "Invariant Violation"
    const parsedErrorType = (errorType ?? (error instanceof Error ? error.name : 'UnknownError'))?.replace(' ', '');
    const errorTypeString = `${errorPrefix} type ${parsedErrorType}`;

    if (isDev() || isTest()) {
      // eslint-disable-next-line no-console
      return console.error(`logError (${errorTypeString}):\n`, errorDetails);
    }

    // splunk
    const logger = getGlobalLogger(errorTypeString);
    if (!logger) {
      throw new Error('RpBootstrap: logger not initialized. You should add a bootstrap to your app');
    }
    logger.error(errorDetails);

    // marlin
    const monitor = getGlobalMonitor();
    monitor?.count({ name: `${errorPrefix} total` });
    monitor?.count({ name: errorTypeString });

    // datadog
    if (!isServerSide) {
      // @ts-ignore (will throw TS error in other apps' builds)
      window.DD_RUM?.addError?.(error, { ...fullContext, errorType: errorTypeString });
    }
  } catch (e) {
    // if somehow this logging function fails, we don't want to crash the app
    // eslint-disable-next-line no-console
    console.error(e);
  }
}
